import styled from "styled-components"
import * as GraphQLClient from "@apollo/client"
import { ConfirmReservation, CreateMasterUser, GetAllPayments, GetAllPrices, GetAllStages, GetCancelManagementItems, GetCancelledReservations, GetGuestsOfStage, GetMasterInitialItems, GetMasterUser, GetOneAheadProductionsInfo, GetReservationsOfStage, GetSeatsOfStage, UpdateReservation } from "@/graphql/documentNode"
import { useCallback, useContext, useMemo, useState } from "react"
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar"
import { useQuery } from "react-query"
import { Guest, GuestType, MasterUser, Payment, Price, Production, Reservation, Seat, SeatType, Stage, UpdateReservationDataInput } from "@/graphql/types"
import Parallelogram from "@/lib/components/common/commonIcon/parallelogram"
import Switcher from "@/lib/components/common/switcher/switcher"
import check from "@/lib/assets/check_red.png"
import { useCheckBox } from "@/lib/ts/hooks/useCheckBox"
import { classifyDevice } from "@/lib/ts/device"
import { InputLabel, TextField } from "@mui/material"
import Button from "@/lib/components/common/button/button"
import dayjs from "dayjs"
import { AuthUser, getCurrentUser, signOut } from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react"
import { useUserRegistration } from "@/lib/ts/hooks/useUserRegistration"
import SelectItemV2 from "@/lib/components/common/input/selectItemV2"
import Header from "@/lib/components/common/header"


const CancelManagement = () => {
    const snackBar = useSnackBar({})
    const snackBar1 = useSnackBar({})
    const snackBar2 = useSnackBar({})
    const [showUserRegistration, setShowUserRegistration] = useState(false)

    const [currentUser, setCurrentUser] = useState<AuthUser | undefined>(undefined)
    const [user, setUser] = useState<MasterUser | undefined>(undefined)

    const [productions, setProductions] = useState([] as Production[])
    const [stages, setStages] = useState([] as Stage[])
    const [reservations, setReservations] = useState([] as Reservation[])
    const [payments, setPayments] = useState([] as Payment[])
    const [prices, setPrices] = useState([] as Price[])
    const [guests, setGuests] = useState([] as Guest[])
    const [guestTypes, setGuestTypes] = useState<GuestType[]>([])
    const [seatTypes, setSeatTypes] = useState<SeatType[]>([])
    const [seats, setSeats] = useState([] as Seat[])
    const [showConfirmed, setShowConfirmed] = useState(true)
    const [showNotConfirmed, setShowNotConfirmed] = useState(true)
    const [searchWord, setSearchWord] = useState("")
    const [filter, setFilter] = useState("")

    const [selectedProductionId, setSelectedProductionId] = useState(-1)
    const [selectedStageId, setSelectedStageId] = useState(-1)

    const device = classifyDevice()
    const isMobile = device === "Android" || device === "ios" || device === "Mobile"

    useQuery([], async () => {
        const currentUser = await getCurrentUser()
        if (!currentUser.signInDetails) {
            setCurrentUser(undefined)
            await signOut()
        } else {
            setCurrentUser(currentUser)
        }
    })

    const userRegistration = useUserRegistration({
        currentUser,
        setUser,
        setCurrentUser,
        signOut,
        user,
    })


    const [getMasterInitialItems] = GraphQLClient.useLazyQuery(GetMasterInitialItems, {
        onCompleted: (data) => {
            setProductions(data.getMasterInitialItems.productions)
            setStages(data.getMasterInitialItems.stages)
            snackBar.onQuerySuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onQueryError()
        }
    })

    useQuery([user], async () => {
        if (user) {
            snackBar.loadStart()
            await getMasterInitialItems({
                variables: {
                    userId: user.id
                }
            })
        }
    })

    const stagesOfProduction: Stage[] | undefined = useMemo(() => {
        const production = productions.find(item => item.id === selectedProductionId)
        if (!production) {
            return undefined
        }
        return stages.filter(stage => stage.productionId === production.id)
    }, [productions, selectedProductionId, stages])

    const [getCancelManagementItems] = GraphQLClient.useLazyQuery(GetCancelManagementItems, {
        onCompleted: (data) => {
            const item = data.getCancelManagementItems
            console.log(item)
            setReservations(item.reservations)
            setGuests(item.guests)
            setSeats(item.seats)
            setGuestTypes(item.guestTypes)
            setSeatTypes(item.seatTypes)
            setPayments(item.payments)
            setPrices(item.prices)
            snackBar.onQuerySuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onQueryError()
        }
    })

    useQuery([selectedStageId], async () => {
        if (selectedStageId >= 0) {
            await getCancelManagementItems({
                variables: {
                    stageId: selectedStageId,
                }
            })
        }
    })

    useQuery([stagesOfProduction], () => {
        if (stagesOfProduction?.length) {
            setSelectedStageId(stagesOfProduction[0].id)
        }
    })


    const [confirmReservation] = GraphQLClient.useMutation(ConfirmReservation, {
        onCompleted: (data) => {
            snackBar.onMutationSuccess()
        },
        onError: (err) => {
            console.log(err)
            snackBar.onMutationError()
        },
    })


    const production = useMemo(() => productions.find(item => item.id === selectedProductionId), [productions, selectedProductionId])
    const stage = useMemo(() => stagesOfProduction?.find(item => item.id === selectedStageId), [selectedStageId, stagesOfProduction])

    const reservationObj = useMemo(() => {
        if (!stage) {
            return <></>
        }
        const includedReservations = reservations.filter((reservation) => reservation.stageId === stage.id)
        console.log(includedReservations)
        return includedReservations.length ? (
            <div>
                <div className="stage-title">
                    {stage.stageName}
                </div>
                <div className="reservations">
                    {
                        includedReservations.map((reservation) => {
                            const includedGuests = guests.filter(guest => guest.reservationId === reservation.id)
                            console.log(includedGuests)
                            const price = includedGuests.length ? includedGuests.map(guest => prices.find(price => price.guestType === guest.guestType && price.seatType === seats.find(seat => seat.id === guest.seatId)?.type)?.price).reduce((pv, cv) => (pv || 0) + (cv || 0)) : 0
                            console.log(price)
                            return price
                                && ((reservation.confirmed && showConfirmed) || (!reservation.confirmed && showNotConfirmed))
                                && (!filter || (filter && reservation.phonetic.includes(filter)))
                                ? (
                                    <div className="reservation">
                                        <p>{reservation.name}</p>
                                        <p className="email">{reservation.email}</p>
                                        <p>キャンセル日時: {reservation.cancellationTime ? dayjs.unix(reservation.cancellationTime).format("YYYY/MM/DD HH:mm") : "-"}</p>
                                        <p>{payments.find(payment => payment.id === reservation.payment)?.name || "-"}</p>
                                        <p>{price}円</p>
                                        <div>
                                            <Switcher
                                                baseColor="white"
                                                defaultValue={reservation.confirmed || false}
                                                labelOn="要返金"
                                                labelOff="対応済"
                                                setCallback={async (value) => {
                                                    snackBar.loadStart()
                                                    await confirmReservation({
                                                        variables: {
                                                            input: {
                                                                id: reservation.id,
                                                                confirm: value,
                                                            }
                                                        }
                                                    }).then((data) => {
                                                        if (data.errors) {
                                                            return
                                                        }
                                                        const reservationsCopy = reservations.map(e => ({ ...e }))
                                                        const index = reservationsCopy.findIndex(item => item.id === reservation.id)
                                                        reservationsCopy[index].confirmed = value
                                                        setReservations(reservationsCopy)
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : <></>
                        })
                    }
                </div>
            </div>
        ) : <></>

    }, [confirmReservation, filter, guests, payments, prices, reservations, seats, showConfirmed, showNotConfirmed, snackBar, stage])


    const confirmTargets = useMemo(() => {
        if (!production) {
            return <></>
        }
        return (
            <div>
                <div className="production-title">
                    <Parallelogram
                        bodyColor="red"
                        shadowColor="black"
                        height={50}
                    >
                        {production.name}
                    </Parallelogram>
                </div>
                {reservationObj}
            </div>
        )

    }, [production, reservationObj])

    const checkBox1 = useCheckBox({ defaultValue: true, onClick: (value) => { setShowConfirmed(value) } })
    const checkBox2 = useCheckBox({ defaultValue: true, onClick: (value) => { setShowNotConfirmed(value) } })


    const selectedProductionIndex = useMemo(() => {
        if (production) {
            return productions.findIndex(p => p.id === production.id)
        }
        return -1
    }, [production, productions])

    const selectedStageIndex = useMemo(() => {
        if (stage && stagesOfProduction) {
            return stagesOfProduction.findIndex(s => s.id === stage.id)
        }
        return -1
    }, [stage, stagesOfProduction])

    const component = userRegistration.showComponent ? userRegistration.registrationComponent : (
        <>
            <Authenticator>
                {({ signOut, user: signInUser }) => {
                    setCurrentUser(signInUser)
                    return currentUser?.signInDetails ? <>
                        <Header />
                        <div className="cancel-body">
                            {userRegistration.userInfoComponent}
                            <div className="title">キャンセル確認</div>
                            <div className="description">
                                <div>キャンセルされた予約を確認できます。</div>
                                <div>返金済・返金未済を管理できます。</div>
                                <div>デフォルトでは、承認済の予約が「要返金」、保留中（入金未済）の予約が「対応済」として表示されます。</div>
                            </div>
                            <div className="options">
                                <div className="select">
                                    <div className="select-item">
                                        <InputLabel>公演</InputLabel>
                                        <SelectItemV2
                                            items={productions}
                                            selectedIndex={selectedProductionIndex}
                                            changeIndex={(production, _) => { setSelectedProductionId(production.id) }}
                                            width={250}
                                        />
                                    </div>
                                    {selectedProductionId >= 0 && !!stagesOfProduction && <div className="select-item">
                                        <InputLabel>ステージ</InputLabel>
                                        <SelectItemV2
                                            items={stagesOfProduction}
                                            selectedIndex={selectedStageIndex}
                                            changeIndex={(stage, _) => { setSelectedStageId(stage.id) }}
                                            width={250}
                                            labelName="stageName"
                                        />
                                    </div>}
                                </div>
                                <div className="checks">
                                    <div className="check">
                                        {checkBox1.component}
                                        <div>要返金の予約を表示</div>
                                    </div>
                                    <div className="check">
                                        <div>{checkBox2.component}</div>
                                        <div>対応済の予約を表示</div>
                                    </div>
                                </div>
                                <div className="search">
                                    <div className="label">
                                        ふりがなで絞り込み
                                    </div>
                                    <TextField
                                        onChange={(event) => setSearchWord(event.target.value)}
                                        variant="filled"
                                        sx={{ backgroundColor: "white" }}
                                        size="small"
                                    />
                                    <div className="buttons">
                                        <Button
                                            buttonType={filter ? "default-red" : "powerful-red-white-shadow"}
                                            label={"絞り込み"}
                                            onClick={() => { setFilter(searchWord) }}
                                        />
                                        <Button
                                            buttonType="powerful-red-white-shadow"
                                            label={"解除"}
                                            onClick={() => {
                                                setFilter("")
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="reservations-wrapper">
                                {
                                    !stages.length ? (
                                        <div>
                                            承認対象を含む公演はありません。
                                        </div>
                                    ) : (
                                        <div className="confirm-targets">
                                            {
                                                confirmTargets
                                            }
                                        </div>
                                    )
                                }
                            </div>
                            {snackBar.component}
                            {snackBar1.component}
                            {snackBar2.component}
                        </div>
                    </> :
                        <div>
                            <Button
                                buttonType="powerful-red-white-shadow"
                                label="サインイン "
                                onClick={() => { signOut?.() }}
                            />
                        </div>
                }
                }
            </Authenticator>
        </>
    )



    return isMobile ? (
        <ConfirmStyle>
            {component}
        </ConfirmStyle>
    ) : (
        <ConfirmStylePC>
            {component}
        </ConfirmStylePC>
    )
}

export default CancelManagement

const ConfirmStylePC = styled.div`
.cancel-body {
    margin-top: 100px;
}
    .title {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        margin: auto;
    }
    .description {
        text-align: center;
        margin: 10px auto 10px auto;
        font-size: 12px;
    }
    .options {
        width: 800px;
        display: flex;
        justify-content: center;
        margin: 16px auto 16px auto;
    }
    .select {
        background-color: white;
        padding: 16px;
        border-radius: 8px;
        .select-item {
            margin: 8px auto 8px auto;
        }
    }
    .checks {
        margin: 10px auto 10px auto;
        text-align: center;
        .check {
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: bold;
            margin: auto;
        }
    }
    .search {
        width: 300px;
        margin: 10px auto 10px auto;
        text-align: center;
        .label {
            font-weight: bold;
        }
        .buttons {
            margin: 10px auto 10px auto;
            display: flex;
            justify-content: center;
            gap: 10px;
        }
    }
    .reservations-wrapper {
        overflow-y: scroll;
        height: 500px;
        background-color: white;
        margin: auto;
        text-align: center;
        padding: 10px 10px 10px 10px;
        width: 90%;
        .confirm-targets {
            color: black;
            .production-title {
                margin: 10px auto 20px auto;
            }
            .stage-title {
                font-weight: bold;
            }
            .reservations {
                margin: auto;
                display: grid;
                grid-template-columns: 320px 320px 320px;
                width: 960px;
                .reservation {
                    background-color: black;
                    width: 300px;
                    margin: 10px auto 10px auto;
                    padding: 10px 0 10px 0;
                    color: white;
                    box-shadow: 5px 5px 0 0 red;
                    .email {
                        height: 60px;
                        word-wrap: break-word;
                    }
                }   
            }
        }
    }
`


const ConfirmStyle = styled.div`
.cancel-body {
    margin-top: 100px;
}
    .title {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        margin: auto;
    }
    .description {
        text-align: center;
        margin: 10px auto 10px auto;
        font-size: 12px;
    }
    .select {
        background-color: white;
        padding: 16px;
        border-radius: 8px;
    }
    .checks {
        margin: 10px auto 10px auto;
        text-align: center;
        .check {
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: bold;
            margin: auto;
        }
    }
    .search {
        width: 300px;
        margin: 10px auto 10px auto;
        text-align: center;
        .label {
            font-weight: bold;
        }
        .buttons {
            margin: 10px auto 10px auto;
            display: flex;
            justify-content: center;
            gap: 10px;
        }
    }
    .reservations-wrapper {
        overflow-y: scroll;
        height: 500px;
        background-color: white;
        margin: auto;
        text-align: center;
        padding: 10px 10px 10px 10px;
        width: 90%;
        .confirm-targets {
            color: black;
            .production-title {
                margin: 10px auto 20px auto;
            }
            .stage-title {
                font-weight: bold;
            }
            .reservation {
                background-color: black;
                width: 300px;
                margin: 10px auto 10px auto;
                padding: 10px 0 10px 0;
                color: white;
                box-shadow: 5px 5px 0 0 red;
                    .email {
                        height: 60px;
                        word-wrap: break-word;
                    }
            }
        }
    }
`
